// PATH: SaberpDeals/frontend/src/pages/PeoplePage.tsx
import { useEffect, useState } from 'react';
import { peopleApi, type Person, type PersonInput } from '@/lib/peopleApi';
import { ledgerApi, type LedgerEntry } from '@/lib/ledgerApi';
import Modal, { fieldLabel, fieldInput } from '@/components/Modal';

const ROLES = ['Sales', 'Technical', 'Both'] as const;

const roleTag: Record<string, string> = {
    Sales: 'bg-forest-soft text-forest',
    Technical: 'bg-amber2-soft text-amber2',
    Both: 'bg-plum-soft text-plum',
};

function initials(name: string) {
    return (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
}
function money(n: number, cur: string) {
    const sym = cur === 'USD' ? '$' : 'IQD ';
    const dp = cur === 'USD' ? 2 : 0;
    return sym + (n || 0).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

const emptyForm: PersonInput = { name: '', defaultRole: 'Both' };

interface Merged { person: Person; earn?: LedgerEntry; }

export default function PeoplePage() {
    const [people, setPeople] = useState<Person[]>([]);
    const [ledger, setLedger] = useState<LedgerEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // detail popup: the ledger entry being viewed (person or house)
    const [detail, setDetail] = useState<LedgerEntry | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<Person | null>(null);
    const [form, setForm] = useState<PersonInput>(emptyForm);
    const [saveErr, setSaveErr] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const [replaceFor, setReplaceFor] = useState<Person | null>(null);
    const [replaceWith, setReplaceWith] = useState<number | ''>('');

    async function load() {
        setLoading(true);
        try {
            const [pp, lg] = await Promise.all([peopleApi.list(), ledgerApi.list()]);
            setPeople(pp); setLedger(lg); setError(null);
        } catch { setError('Failed to load people.'); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openAdd() { setEditing(null); setForm(emptyForm); setSaveErr(null); setShowForm(true); }
    function openEdit(p: Person) { setEditing(p); setForm({ name: p.name, defaultRole: p.defaultRole }); setSaveErr(null); setShowForm(true); }

    async function save() {
        setSaving(true); setSaveErr(null);
        try {
            if (editing) await peopleApi.update(editing.id, form);
            else await peopleApi.create(form);
            setShowForm(false); await load();
        } catch (e: any) { setSaveErr(e?.response?.data?.error ?? 'Could not save.'); }
        finally { setSaving(false); }
    }

    async function toggle(p: Person) {
        try { await peopleApi.toggle(p.id); await load(); } catch { /* ignore */ }
    }

    function openReplace(p: Person) { setReplaceFor(p); setReplaceWith(p.replacedByPersonId ?? ''); }
    async function saveReplace() {
        if (!replaceFor) return;
        try {
            await peopleApi.setReplacement(replaceFor.id, replaceWith === '' ? null : Number(replaceWith));
            setReplaceFor(null); await load();
        } catch { /* ignore */ }
    }

    const houses = ledger.filter((e) => e.kind !== 'Person');
    const merged: Merged[] = people.map((p) => ({
        person: p,
        earn: ledger.find((e) => e.kind === 'Person' && e.personId === p.id),
    }));

    const houseAv = (kind: string) => kind === 'Company' ? 'bg-paper-2 text-ink-2' : 'bg-plum-soft text-plum';
    const isHouse = detail && detail.kind !== 'Person';

    return (
        <div className="mt-6">
            <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="font-disp text-[21px] font-semibold tracking-tight">People &amp; earnings</div>
                    <div className="text-[13px] text-ink-3 mt-0.5 max-w-xl">
                        What each is owed across all deals. Click a card for the per-customer breakdown.
                    </div>
                </div>
                <button onClick={openAdd} className="bg-forest text-paper text-[13px] font-semibold px-4 py-2.5 rounded-lg hover:bg-forest-2 transition">
                    + Add person
                </button>
            </div>

            {error && <div className="mb-4 rounded-lg bg-clay-soft border border-clay/30 px-4 py-3 text-sm text-clay">{error}</div>}

            {loading ? (
                <div className="text-ink-3 text-sm py-16 text-center">Loading…</div>
            ) : (
                <>
                    {/* HOUSE: Company + Owner — uniform cards, click → popup */}
                    {houses.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-4">
                            {houses.map((e) => (
                                <button key={`${e.kind}-${e.currency}`} onClick={() => setDetail(e)}
                                    className="text-left bg-card border border-line rounded-2xl p-4 hover:border-forest-2 hover:shadow-sm transition">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold font-mono flex-none ${houseAv(e.kind)}`}>
                                            {e.kind === 'Company' ? 'CO' : 'OW'}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-[15px] truncate">{e.kind === 'Owner' ? 'Owner (you)' : 'Company'}</div>
                                            <div className="text-[11px] text-ink-3 font-mono">{e.dealCount} deal{e.dealCount !== 1 ? 's' : ''} · {e.currency}</div>
                                        </div>
                                    </div>
                                    <div className="font-disp text-2xl font-semibold mt-3">{money(e.total, e.currency)}</div>
                                    <div className="text-[11px] text-ink-3 mt-1 font-mono">Click to view breakdown →</div>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* PEOPLE: uniform cards. Card body click → popup; action buttons at bottom */}
                    {merged.length === 0 ? (
                        <div className="text-center py-16 text-ink-3">
                            <div className="font-disp text-lg font-semibold text-ink-2 mb-1.5">No people yet</div>
                            <div className="text-sm">Add the people who sell and implement your deals.</div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                            {merged.map(({ person: p, earn }) => {
                                const cur = earn?.currency ?? 'USD';
                                return (
                                    <div key={p.id} className={`bg-card border border-line rounded-2xl p-4 ${!p.isActive ? 'opacity-60' : ''}`}>
                                        <div className={`flex items-center gap-3 ${earn ? 'cursor-pointer' : ''}`} onClick={() => earn && setDetail(earn)}>
                                            <div className="w-9 h-9 rounded-full bg-forest-soft text-forest flex items-center justify-center text-xs font-semibold font-mono flex-none">
                                                {initials(p.name)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-semibold text-[15px] truncate">{p.name}</div>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <span className="inline-flex items-center gap-1 text-[11px] text-ink-3">
                                                        <span className={`w-1.5 h-1.5 rounded-full ${p.isActive ? 'bg-forest-2' : 'bg-line-2'}`} />
                                                        {p.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <span className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded-xl ${roleTag[p.defaultRole]}`}>{p.defaultRole}</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-mono font-semibold text-[15px]">{earn ? money(earn.total, cur) : money(0, 'USD')}</div>
                                                <div className="text-[10px] text-ink-3 font-mono">{earn ? `${earn.dealCount} deal${earn.dealCount !== 1 ? 's' : ''} →` : 'no deals'}</div>
                                            </div>
                                        </div>

                                        {p.replacedByName && (
                                            <div className="text-[11px] text-ink-3 mt-2.5 font-mono">Future renewals → {p.replacedByName}</div>
                                        )}

                                        <div className="flex gap-3 mt-3 pt-3 border-t border-paper-2 text-[12.5px]">
                                            <button onClick={() => openEdit(p)} className="text-ink-3 hover:text-forest">Edit</button>
                                            <button onClick={() => toggle(p)} className="text-ink-3 hover:text-clay">{p.isActive ? 'Deactivate' : 'Reactivate'}</button>
                                            <button onClick={() => openReplace(p)} className="text-ink-3 hover:text-plum ml-auto">Set replacement</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* DETAIL POPUP */}
            {detail && (
                <Modal
                    title={detail.kind === 'Owner' ? 'Owner (you) — earnings' : detail.kind === 'Company' ? 'Company — earnings' : `${detail.name} — earnings`}
                    onClose={() => setDetail(null)} maxWidth="max-w-lg">
                    <div className="flex items-baseline justify-between mb-4">
                        <span className="font-disp text-3xl font-semibold">{money(detail.total, detail.currency)}</span>
                        <span className="text-[12px] text-ink-3 font-mono">{detail.dealCount} deal{detail.dealCount !== 1 ? 's' : ''} · {detail.currency}</span>
                    </div>
                    <table className="w-full border-collapse text-[13px]">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wide text-ink-3 border-b border-line">
                                <td className="pb-1.5">Customer</td>
                                {isHouse ? (
                                    <td className="pb-1.5 text-right">Amount</td>
                                ) : (
                                    <>
                                        <td className="pb-1.5 text-right">Sales</td>
                                        <td className="pb-1.5 text-right">Technical</td>
                                        <td className="pb-1.5 text-right">Total</td>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {detail.customers.map((c, i) => (
                                <tr key={i} className="border-b border-line">
                                    <td className="py-2">{c.customer}</td>
                                    {isHouse ? (
                                        <td className="py-2 text-right font-mono">{money(c.total, detail.currency)}</td>
                                    ) : (
                                        <>
                                            <td className="py-2 text-right font-mono">{money(c.sales, detail.currency)}</td>
                                            <td className="py-2 text-right font-mono">{money(c.technical, detail.currency)}</td>
                                            <td className="py-2 text-right font-mono">{money(c.total, detail.currency)}</td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            <tr className="font-semibold">
                                <td className="py-2">Total</td>
                                {isHouse ? (
                                    <td className="py-2 text-right font-mono">{money(detail.total, detail.currency)}</td>
                                ) : (
                                    <>
                                        <td className="py-2 text-right font-mono">{money(detail.sales, detail.currency)}</td>
                                        <td className="py-2 text-right font-mono">{money(detail.technical, detail.currency)}</td>
                                        <td className="py-2 text-right font-mono">{money(detail.total, detail.currency)}</td>
                                    </>
                                )}
                            </tr>
                        </tbody>
                    </table>
                    {isHouse && (
                        <div className="text-[12px] text-ink-3 mt-4 pt-3 border-t border-line font-mono">
                            New business {money(detail.newBusiness, detail.currency)} · Renewal/support {money(detail.renewal + detail.support, detail.currency)}
                        </div>
                    )}
                </Modal>
            )}

            {showForm && (
                <Modal title={editing ? 'Edit person' : 'Add person'} onClose={() => setShowForm(false)}>
                    {saveErr && <div className="mb-3 rounded-lg bg-clay-soft border border-clay/30 px-3 py-2 text-[13px] text-clay">{saveErr}</div>}

                    <label className={fieldLabel}>Name</label>
                    <input className={`${fieldInput} mb-3`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aland K." autoFocus />

                    <label className={fieldLabel}>Default role</label>
                    <select className={`${fieldInput} mb-5`} value={form.defaultRole} onChange={(e) => setForm({ ...form, defaultRole: e.target.value as any })}>
                        {ROLES.map((r) => <option key={r}>{r}</option>)}
                    </select>

                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-ink-2 rounded-lg border border-line-2 hover:bg-paper">Cancel</button>
                        <button disabled={saving || !form.name} onClick={save} className="px-4 py-2 text-sm font-semibold text-paper bg-forest rounded-lg hover:bg-forest-2 disabled:opacity-50">
                            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add person'}
                        </button>
                    </div>
                </Modal>
            )}

            {replaceFor && (
                <Modal title={`Replacement for ${replaceFor.name}`} onClose={() => setReplaceFor(null)}>
                    <p className="text-[13px] text-ink-3 mb-4">
                        When {replaceFor.name} leaves, their customers' future renewal commissions transfer to the person you pick. Past payouts are unchanged.
                    </p>
                    <label className={fieldLabel}>Replaced by</label>
                    <select className={`${fieldInput} mb-5`} value={replaceWith} onChange={(e) => setReplaceWith(e.target.value ? Number(e.target.value) : '')}>
                        <option value="">— None —</option>
                        {people.filter((x) => x.id !== replaceFor.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setReplaceFor(null)} className="px-4 py-2 text-sm text-ink-2 rounded-lg border border-line-2 hover:bg-paper">Cancel</button>
                        <button onClick={saveReplace} className="px-4 py-2 text-sm font-semibold text-paper bg-forest rounded-lg hover:bg-forest-2">Save</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
